import { F, getIntlTranslation } from '@/utils/intl';
import { UserOutlined } from '@ant-design/icons';
import { Button, Input, InputRef, Popover, Tooltip } from 'antd';
import React, { useCallback, useMemo, useRef, useState } from 'react';
import { useIntl } from 'umi';
import { EmailVariableGroup } from './EmailBodyEditor/types';
import VariableOptionList from './VariableOptionList';
import {
  buildVariableOptions,
  filterVariableOptions,
  VariableOption,
} from './variableOptions';

import styles from './VariableInsertButton.less';

interface VariableInsertButtonProps {
  /** From GET /email-variables (plus the journey theme), as the editor gets them. */
  variableGroups?: EmailVariableGroup[];
  /** Namespace and name of the pick — the shape `buildVariableSnippet` takes. */
  onInsert: (namespace: string, name: string) => void;
  /**
   * Text beside the icon, for a toolbar with room for it. Omitted, the button is
   * the icon alone — which is what fits on the end of a subject field.
   */
  label?: React.ReactNode;
  disabled?: boolean;
  /** Where the list opens; a toolbar button at the right edge wants it flipped. */
  placement?: 'bottomLeft' | 'bottomRight';
}

/**
 * The one way to pick a variable, wherever one can be inserted: the subject and
 * pretext fields, the Twig source toolbar and the rich-text toolbar.
 *
 * It replaced a grouped antd menu, which was the wrong shape for a catalog this size:
 * the answer to "how do I put the customer's first name in here" is a search box, not
 * a walk through `theme.colors`. Rows show the friendly name over the exact text a
 * pick inserts, so the result is never a surprise.
 *
 * It is the discoverable half of a pair — typing `{{` in any of those surfaces opens
 * the same list inline (plus the partner's saved texts, which have their own
 * affordances here), for people who already know what they are after.
 */
const VariableInsertButton: React.FC<VariableInsertButtonProps> = ({
  variableGroups = [],
  onInsert,
  label,
  disabled,
  placement = 'bottomRight',
}) => {
  const intl = useIntl();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const searchRef = useRef<InputRef>(null);

  const options = useMemo(
    () => buildVariableOptions(variableGroups),
    [variableGroups],
  );
  const matches = useMemo(
    () => filterVariableOptions(options, query),
    [options, query],
  );

  const insert = useCallback(
    (option: VariableOption) => {
      onInsert(option.namespace, option.name);
      setOpen(false);
      setQuery('');
    },
    [onInsert],
  );

  const handleOpenChange = useCallback((next: boolean) => {
    setOpen(next);
    if (!next) return;
    setQuery('');
    // The popover keeps its content mounted between openings, so `autoFocus`
    // would only ever fire once — focus it on every open instead.
    requestAnimationFrame(() => searchRef.current?.focus());
  }, []);

  if (options.length === 0) return null;

  const content = (
    <div className={styles.picker}>
      <Input
        ref={searchRef}
        size="small"
        allowClear
        value={query}
        placeholder={getIntlTranslation(intl, {
          id: 'pages.emailCenter.variableInsert.search',
          defaultMessage: 'Search variables…',
        })}
        onChange={(event) => setQuery(event.target.value)}
        // Type three letters, hit Enter: the common case is one obvious match.
        onPressEnter={() => matches[0] && insert(matches[0])}
      />

      <VariableOptionList
        options={matches}
        onPick={insert}
        empty={
          <F
            id="pages.emailCenter.variableInsert.noMatches"
            defaultMessage="No variable matches that."
          />
        }
      />

      <span className={styles.hint}>
        <F
          id="pages.emailCenter.variableInsert.hint"
          defaultMessage="Filled in per recipient when the email is sent. Typing {opener} offers the same list; {keyword} offers your saved texts."
          values={{
            opener: <code>{'{{'}</code>,
            keyword: <code>{'{{ trans'}</code>,
          }}
        />
      </span>
    </div>
  );

  const title = getIntlTranslation(intl, {
    id: 'pages.emailCenter.variableInsert.tooltip',
    defaultMessage: 'Insert a variable, e.g. the recipient’s first name',
  });

  return (
    <Popover
      open={open}
      onOpenChange={handleOpenChange}
      trigger="click"
      placement={placement}
      content={content}
    >
      <Tooltip title={title}>
        <Button
          className={label ? undefined : styles.trigger}
          size="small"
          disabled={disabled}
          aria-label={title}
          icon={<UserOutlined />}
        >
          {label}
        </Button>
      </Tooltip>
    </Popover>
  );
};

export default React.memo(VariableInsertButton);
